'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

type TabsVariant = 'default' | 'segmented';

const TabsVariantContext = React.createContext<TabsVariant>('default');

function Tabs({
	variant = 'default',
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
	return (
		<TabsVariantContext.Provider value={variant}>
			<TabsPrimitive.Root data-slot='tabs' className={cn('flex flex-col gap-2', className)} {...props} />
		</TabsVariantContext.Provider>
	);
}

function TabsList({ className, children, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
	const segmented = React.useContext(TabsVariantContext) === 'segmented';
	const ref = React.useRef<HTMLDivElement>(null);
	const [indicator, setIndicator] = React.useState({ left: 0, width: 0, ready: false });

	// Segmented variant draws a single sliding pill behind the active trigger
	// (instead of each trigger painting its own active background). We measure
	// the active trigger and reposition the indicator whenever Radix flips its
	// data-state or the list reflows.
	React.useLayoutEffect(() => {
		if (!segmented || !ref.current) return;
		const list = ref.current;
		const measure = () => {
			const active = list.querySelector<HTMLElement>('[data-state="active"]');
			if (active) setIndicator({ left: active.offsetLeft, width: active.offsetWidth, ready: true });
		};
		measure();
		const mo = new MutationObserver(measure);
		mo.observe(list, { attributes: true, attributeFilter: ['data-state'], subtree: true });
		const ro = new ResizeObserver(measure);
		ro.observe(list);
		return () => {
			mo.disconnect();
			ro.disconnect();
		};
	}, [segmented]);

	return (
		<TabsPrimitive.List
			ref={ref}
			data-slot='tabs-list'
			className={cn(
				segmented
					? 'relative isolate inline-flex w-fit items-center rounded-full bg-gray-100 p-0.5 text-muted-foreground'
					: 'bg-gray-100 text-muted-foreground border inline-flex  w-fit items-center justify-center rounded-full overflow-clip p-px',
				className
			)}
			{...props}
		>
			{segmented && (
				<span
					aria-hidden
					className={cn(
						'absolute top-0.5 bottom-0.5 left-0 rounded-full border border-border bg-background shadow-sm ease-out',
						indicator.ready ? 'transition-[transform,width] duration-300' : 'opacity-0'
					)}
					style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
				/>
			)}
			{children}
		</TabsPrimitive.List>
	);
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
	const segmented = React.useContext(TabsVariantContext) === 'segmented';
	return (
		<TabsPrimitive.Trigger
			data-slot='tabs-trigger'
			className={cn(
				segmented
					? "relative z-10 inline-flex items-center justify-center gap-1.5 rounded-full border border-transparent px-3 py-1 text-xs font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
					: "data-[state=active]:bg-transparent hover:cursor-pointer data-[state=active]:text-foreground rounded-full focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-muted-foreground inline-flex h-7 flex-1 items-center justify-center gap-1.5 border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-border [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-[state=active]:drop-shadow-sm",
				className
			)}
			{...props}
		/>
	);
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
	return <TabsPrimitive.Content data-slot='tabs-content' className={cn('flex-1 outline-none', className)} {...props} />;
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
